/**
 * CostChartsPanel — Renders AnnualCostChart and CumulativeCostChart
 * from engine result data when available.
 */

import { useMemo, useState } from 'react';
import { AnnualCostChart, CumulativeCostChart } from '@system-rec/decision-ui';
import type { AnnualCostBarData, CumulativeCostDataPoint } from '@system-rec/decision-ui';
import type { DecisionEngineResult } from '../../api/decisionEngine';

interface Props {
  engineResult: DecisionEngineResult;
}

export function CostChartsPanel({ engineResult }: Props) {
  const [activeTab, setActiveTab] = useState<'annual' | 'cumulative'>('annual');

  // Map engine annual cost data to AnnualCostBarData[]
  const annualBars = useMemo<AnnualCostBarData[]>(() => {
    const data = engineResult.annualCostData;
    if (!data || !data.bars || data.bars.length === 0) return [];

    return data.bars.map(bar => ({
      label: bar.label,
      cost: bar.annualCost_gbp,
      delta: bar.deltaVsBaseline_gbp ?? undefined,
      isBaseline: bar.isBaseline,
    }));
  }, [engineResult.annualCostData]);

  // Map engine cumulative cost data to CumulativeCostDataPoint[]
  const cumulativeData = useMemo<CumulativeCostDataPoint[]>(() => {
    const data = engineResult.costOverTimeData;
    if (!data || !data.series || data.series.length === 0) return [];

    // Get max year across all series
    const maxYear = Math.max(
      ...data.series.flatMap(s => s.data.map(d => d.year))
    );
    if (maxYear <= 0) return [];

    const points: CumulativeCostDataPoint[] = [];
    for (let year = 0; year <= maxYear; year++) {
      const point: CumulativeCostDataPoint = { year };
      for (const series of data.series) {
        const dp = series.data.find(d => d.year === year);
        const key = series.isBaseline ? 'baseline' : series.label;
        point[key] = dp?.cumulativeCost ?? undefined;
      }
      points.push(point);
    }
    return points;
  }, [engineResult.costOverTimeData]);

  const hasAnnual = annualBars.length > 0;
  const hasCumulative = cumulativeData.length > 0;

  if (!hasAnnual && !hasCumulative) {
    return (
      <div className="cost-charts-panel cost-charts-panel--empty">
        <div className="cost-charts-panel__skeleton">
          <div className="cost-charts-panel__skeleton-bar" />
          <div className="cost-charts-panel__skeleton-bar cost-charts-panel__skeleton-bar--short" />
          <div className="cost-charts-panel__skeleton-bar cost-charts-panel__skeleton-bar--med" />
          <p className="cost-charts-panel__skeleton-text">
            Cost charts will appear when the engine produces cost data.
            Ensure system details and property data are filled in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cost-charts-panel">
      <div className="cost-charts-panel__header">
        <h3 className="cost-charts-panel__title">Cost analysis</h3>
        <div className="cost-charts-panel__tabs">
          {hasAnnual && (
            <button
              className={`cost-charts-panel__tab${activeTab === 'annual' ? ' cost-charts-panel__tab--active' : ''}`}
              onClick={() => setActiveTab('annual')}
            >
              Annual costs
            </button>
          )}
          {hasCumulative && (
            <button
              className={`cost-charts-panel__tab${activeTab === 'cumulative' ? ' cost-charts-panel__tab--active' : ''}`}
              onClick={() => setActiveTab('cumulative')}
            >
              Cumulative costs
            </button>
          )}
        </div>
      </div>

      <div className="cost-charts-panel__chart">
        {activeTab === 'annual' && hasAnnual && (
          <AnnualCostChart data={annualBars} />
        )}
        {activeTab === 'cumulative' && hasCumulative && (
          <CumulativeCostChart data={cumulativeData} />
        )}
      </div>
    </div>
  );
}
